export type ExistingPhotoOrderItem = {
  kind: 'existing'
  /**
   * Unique stable key for drag ordering.
   * (Do not assume URL uniqueness across all time; id should be unique per item.)
   */
  id: string
  url: string
}

export type NewPhotoOrderItem = {
  kind: 'new'
  /**
   * Unique stable key for drag ordering.
   */
  id: string
}

export type PhotoOrderItem = ExistingPhotoOrderItem | NewPhotoOrderItem

export function toExistingPhotoItems(urls: string[]): ExistingPhotoOrderItem[] {
  return (urls || []).filter(Boolean).map((url, idx) => ({
    kind: 'existing' as const,
    id: `existing_${idx}`,
    url,
  }))
}

export function extractExistingUrls(items: PhotoOrderItem[]): string[] {
  return (items || []).flatMap((it) => (it.kind === 'existing' ? [it.url] : []))
}

export function sameStringArray(a: string[], b: string[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

export function hasExistingPhotosChanged(originalUrls: string[], items: PhotoOrderItem[]): boolean {
  return !sameStringArray(originalUrls || [], extractExistingUrls(items))
}

export function applySavedPhotoUrlsToVehicle(
  vehicle: Record<string, unknown>,
  dealerPhotos: string[],
): Record<string, unknown> {
  return {
    ...vehicle,
    dealer_photos: [...(dealerPhotos || [])],
  }
}

/**
 * Pull shopper-facing photo URLs from a Chrome/Data Hub prefill payload.
 * Prefers dealer_photos, then stock_photos. Accepts string URLs or { Source|source|url }.
 */
export function extractPrefillPhotoUrls(prefill: Record<string, unknown> | null | undefined): string[] {
  if (!prefill || typeof prefill !== 'object') return []

  const candidates = [prefill.dealer_photos, prefill.stock_photos]
  for (const candidate of candidates) {
    if (!Array.isArray(candidate) || candidate.length === 0) continue
    const urls = candidate
      .map((entry) => {
        if (typeof entry === 'string') return entry.trim()
        if (entry && typeof entry === 'object') {
          const record = entry as Record<string, unknown>
          for (const key of ['Source', 'source', 'url', 'href', 'VisibleUrl', 'visibleUrl']) {
            const value = record[key]
            if (typeof value === 'string' && value.trim()) return value.trim()
          }
        }
        return ''
      })
      .filter(Boolean)
    if (urls.length > 0) return urls
  }

  return []
}

/** One shopper-facing color's worth of prefill gallery URLs, grouped by ise-srp-back. */
export type PhotoColorGroup = {
  colorCode: string
  /** Human-readable color name (e.g. "White"), or "Standard" for uncolored views. */
  colorName: string
  /** Normalized (no "#") hex swatch for this color, when known. */
  hex: string
  urls: string[]
}

/**
 * Extract Chrome gallery color groups from a prefill payload's `__photo_color_groups`
 * (added by ise-srp-back's custom-feed-prefill API), for a color-based photo picker.
 * Groups with no URLs are dropped; malformed entries are ignored defensively.
 */
export function extractPrefillColorGroups(
  prefill: Record<string, unknown> | null | undefined,
): PhotoColorGroup[] {
  if (!prefill || typeof prefill !== 'object') return []

  const raw = (prefill as Record<string, unknown>).__photo_color_groups
  if (!Array.isArray(raw)) return []

  return raw.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return []
    const group = entry as Record<string, unknown>
    const urls = Array.isArray(group.urls)
      ? group.urls.filter((url): url is string => typeof url === 'string' && url.trim() !== '')
      : []
    if (urls.length === 0) return []

    return [{
      colorCode: typeof group.colorCode === 'string' ? group.colorCode : '',
      colorName: typeof group.colorName === 'string' && group.colorName.trim() ? group.colorName : 'Standard',
      hex: typeof group.hex === 'string' ? group.hex : '',
      urls,
    }]
  })
}

type DataHubGalleryImage = Record<string, unknown>

function galleryShotOrder(image: DataHubGalleryImage): number {
  const shot = Number.parseInt(String(image.shotCode ?? ''), 10)
  return Number.isNaN(shot) ? Number.MAX_SAFE_INTEGER : shot
}

/** Resolve the `images`-bearing object from a raw Data Hub vehicle payload. */
function dataHubImagesRoot(datahub: Record<string, unknown>): Record<string, unknown> {
  if (datahub.images && typeof datahub.images === 'object') return datahub
  if (datahub.data && typeof datahub.data === 'object') return datahub.data as Record<string, unknown>
  return datahub
}

function dataHubMatchingImages(
  images: DataHubGalleryImage[],
  width: number,
  background: string,
): DataHubGalleryImage[] {
  const bg = background.toLowerCase()
  const matches = images.filter((image) => {
    const imageWidth = Number(image.width)
    const imageBg = String(image.backgroundDescription ?? '').toLowerCase()
    return imageWidth === width && (imageBg === bg || imageBg === 'transparent')
  })
  // Prefer the requested background over "transparent" when both exist.
  const preferredMatches = matches.filter(
    (image) => String(image.backgroundDescription ?? '').toLowerCase() === bg,
  )
  return preferredMatches.length > 0 ? preferredMatches : matches
}

function dataHubImageUrls(images: DataHubGalleryImage[]): string[] {
  const sorted = [...images].sort((left, right) => galleryShotOrder(left) - galleryShotOrder(right))
  const seen = new Set<string>()
  const urls: string[] = []
  for (const image of sorted) {
    const href = typeof image.href === 'string' ? image.href.trim() : ''
    if (!href || seen.has(href)) continue
    seen.add(href)
    urls.push(href)
  }
  return urls
}

/**
 * Client-side port of ise-srp-back's `getChromeGalleryColorGroups`, for calling Data Hub
 * directly from the FE (e.g. while the backend's `__photo_color_groups` isn't deployed yet,
 * or for enhancements the backend doesn't map). Groups every gallery URL by exterior color.
 */
export function extractDataHubColorGroups(
  datahub: Record<string, unknown> | null | undefined,
  width = 1280,
  background = 'White',
): PhotoColorGroup[] {
  if (!datahub || typeof datahub !== 'object') return []

  const root = dataHubImagesRoot(datahub)
  const images = root.images
  if (!images || typeof images !== 'object' || Array.isArray(images)) return []

  const record = images as Record<string, unknown>
  const views = Array.isArray(record.views)
    ? (record.views as DataHubGalleryImage[])
    : Array.isArray(record.view)
      ? (record.view as DataHubGalleryImage[])
      : []
  const colorized = Array.isArray(record.colorized) ? (record.colorized as DataHubGalleryImage[]) : []

  const viewImages = dataHubMatchingImages(views, width, background)
  const colorizedImages = dataHubMatchingImages(colorized, width, background)

  const exteriorColorsRaw = Array.isArray(root.exteriorColors) ? root.exteriorColors : []
  const colorNames = exteriorColorsRaw.flatMap((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return []
    const option = value as Record<string, unknown>
    const code = String(option.colorCode ?? '').trim().toUpperCase()
    const hex = String(option.rgbHexValue ?? '').replace(/^#/, '').trim().toUpperCase()
    if (!code && !hex) return []
    const name =
      (typeof option.genericDesc === 'string' && option.genericDesc.trim()) ||
      (typeof option.description === 'string' && option.description.trim()) ||
      ''
    return [{ code, hex, name }]
  })

  // Keyed by colorName (not code): Chrome often uses several distinct paint codes
  // for the same shopper-facing name (e.g. "Black" via codes BZ/BV/BK), and those
  // should collapse into a single group rather than appearing as duplicate buttons.
  const groups = new Map<string, PhotoColorGroup>()

  const standardUrls = dataHubImageUrls(viewImages)
  if (standardUrls.length > 0) {
    groups.set('Standard', { colorCode: '', colorName: 'Standard', hex: '', urls: standardUrls })
  }

  const byCode = new Map<string, DataHubGalleryImage[]>()
  for (const image of colorizedImages) {
    const code =
      String(image.primaryColorOptionCode ?? '').trim().toUpperCase() ||
      String(image.primaryRGBHexCode ?? '').replace(/^#/, '').trim().toUpperCase()
    if (!code) continue
    const bucket = byCode.get(code)
    if (bucket) bucket.push(image)
    else byCode.set(code, [image])
  }

  for (const [code, bucketImages] of byCode) {
    const urls = dataHubImageUrls(bucketImages)
    if (urls.length === 0) continue

    const match = colorNames.find((candidate) => candidate.code === code || candidate.hex === code)
    const hex =
      match?.hex || String(bucketImages[0]?.primaryRGBHexCode ?? '').replace(/^#/, '').trim().toUpperCase()
    const colorName = match?.name || (hex ? `Color #${hex}` : code)
    const colorCode = match?.code || (/^[0-9A-F]{6}$/.test(code) ? '' : code)

    const existing = groups.get(colorName)
    if (existing) {
      const urlSet = new Set(existing.urls)
      for (const url of urls) urlSet.add(url)
      existing.urls = [...urlSet]
      if (!existing.hex && hex) existing.hex = hex
      // Multiple distinct codes now share this name — colorCode no longer identifies a single option.
      if (existing.colorCode && existing.colorCode !== colorCode) existing.colorCode = ''
    } else {
      groups.set(colorName, { colorCode, colorName, hex, urls })
    }
  }

  return [...groups.values()].sort((left, right) => {
    if (left.colorName === 'Standard') return -1
    if (right.colorName === 'Standard') return 1
    return left.colorName.localeCompare(right.colorName)
  })
}

/**
 * Builds the final ordered dealer_photos array by replacing each "new" item with the
 * uploaded public URL for that item's id, preserving the combined order.
 *
 * Throws if a "new" item is missing its uploaded URL, to avoid silently dropping photos.
 */
export function buildFinalPhotoUrls(
  items: PhotoOrderItem[],
  uploadedById: Record<string, string>,
): string[] {
  const out: string[] = []
  for (const it of items || []) {
    if (it.kind === 'existing') {
      if (it.url) out.push(it.url)
      continue
    }

    const url = uploadedById?.[it.id]
    if (!url) {
      throw new Error(`Missing uploaded URL for photo id "${it.id}"`)
    }
    out.push(url)
  }
  return out
}

